import { createFileRoute } from "@tanstack/react-router";
import { useVehicles } from "@/hooks/queries";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Truck, Wifi, WifiOff, Clock, Activity } from "lucide-react";
import { WaitingBackend } from "@/components/waiting-backend";

export const Route = createFileRoute("/dashboard")({
  component: Dashboard,
  head: () => ({ meta: [{ title: "Dashboard — Wp Rastreamento" }] }),
});

function Dashboard() {
  const { data: vehicles, isLoading, error } = useVehicles();

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-sm text-muted-foreground">Visão geral da sua frota em tempo real.</p>
      </div>
      {isLoading || error || !vehicles ? <WaitingBackend error={error} /> : <Stats vehicles={vehicles} />}
    </div>
  );
}

function Stats({ vehicles }: { vehicles: import("@/types").Vehicle[] }) {
  const total = vehicles.length;
  const online = vehicles.filter((v) => v.status === "online").length;
  const offline = vehicles.filter((v) => v.status === "offline").length;
  const idle = vehicles.filter((v) => v.status === "idle").length;
  const lastUpdate = vehicles.length
    ? vehicles.reduce((max, v) => (new Date(v.lastConnection) > new Date(max) ? v.lastConnection : max), vehicles[0].lastConnection)
    : null;

  const cards = [
    { title: "Total de veículos", value: total, icon: Truck, color: "text-primary", bg: "bg-primary/10" },
    { title: "Veículos online", value: online, icon: Wifi, color: "text-emerald-600", bg: "bg-emerald-500/10" },
    { title: "Veículos offline", value: offline, icon: WifiOff, color: "text-rose-600", bg: "bg-rose-500/10" },
    { title: "Última atualização", value: lastUpdate ? new Date(lastUpdate).toLocaleTimeString("pt-BR") : "—", icon: Clock, color: "text-amber-600", bg: "bg-amber-500/10" },
  ];

  return (
    <>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {cards.map((c) => (
          <Card key={c.title}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">{c.title}</CardTitle>
              <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${c.bg}`}>
                <c.icon className={`h-4 w-4 ${c.color}`} />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{c.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Activity className="h-4 w-4" /> Status atual da frota
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-3">
            <StatusRow label="Online" value={online} total={total} tone="emerald" />
            <StatusRow label="Ocioso" value={idle} total={total} tone="amber" />
            <StatusRow label="Offline" value={offline} total={total} tone="rose" />
          </div>
          {online > 0 && (
            <div className="mt-6 space-y-2">
              <p className="text-xs font-medium uppercase text-muted-foreground">Veículos ativos</p>
              <div className="flex flex-wrap gap-2">
                {vehicles.filter((v) => v.status === "online").map((v) => (
                  <Badge key={v.id} variant="secondary" className="gap-1.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    {v.name} · {v.speed} km/h
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}

function StatusRow({ label, value, total, tone }: { label: string; value: number; total: number; tone: "emerald" | "amber" | "rose" }) {
  const pct = total ? Math.round((value / total) * 100) : 0;
  const bg = tone === "emerald" ? "bg-emerald-500" : tone === "amber" ? "bg-amber-500" : "bg-rose-500";
  return (
    <div className="rounded-lg border p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-medium">{label}</span>
        <span className="text-sm text-muted-foreground">{value} ({pct}%)</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-muted">
        <div className={`h-full ${bg}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
